import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Alert, Box, CircularProgress, Divider, Paper, TextField as MuiTextField, Typography, } from "@mui/material";
import taskerService, {
  type TaskerTask, } from "../../../services/taskerService";
import { readErrorMessage } from "../utils/mapBeErrors";
import { optionDisplayName } from "../utils/fieldFormat";
import { deriveCommercial } from "./commercialTerms";
import { ReviewerActionsFooter } from "./ReviewerActionsFooter";
import { useRemarksMaxLength } from "./useRemarksMaxLength";
import { CharCounter } from "../components/CharCounter";
import { clampText } from "../utils/textLimits";
import { useReviewerTaskType } from "./reviewerQueries";
import { transitionForms } from "./taskConfig";
import { VmEnrichmentSummary } from "./VmEnrichmentSummary";
import { PriorReviewerRemarks } from "./PriorReviewerRemarks";
import {
  REVIEWER_APPROVED_MSG, REVIEWER_REJECTED_MSG, REVIEWER_SEND_BACK_MSG_TO_VM, REVIEWER_REMARKS_FIELD, reviewerFormId } from "./reviewerRoles";
import { type OnTransitionSubmitted } from "./ReviewerRouter";

type Props = { task: TaskerTask; onSubmitted: OnTransitionSubmitted };

const CM_HIDDEN_SECTIONS = new Set(["commercial_terms"]);

// CM doesn't render the assigned-reviewers card VmReviewPanel uses, so keep
// the routing emails visible in the enrichment table — otherwise the CM has
// no way to see who VM routed BizFin to. Only drop the two demo-removal
// fields VmReviewPanel also hides.
const CM_HIDDEN_FIELDS = new Set([
  "three_vendor_quotes_available",
  "auto_invoice_validate",
]);

function LabelValue({ label, value }: { label: string; value: string }) {
  return (
    <Box>
      <Typography sx={{ fontSize: 12, color: "text.secondary", mb: 0.25 }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: 14, fontWeight: 500 }}>{value}</Typography>
    </Box>
  );
}

function CommercialTermsCard({
  creditDays,
  patternName,
  basisName,
  basisLabel,
}: {
  creditDays?: string;
  patternName?: string;
  basisName?: string;
  basisLabel?: string;
}) {
  const derived = deriveCommercial(basisName, patternName, creditDays);
  const advanceCombined =
    derived.advanceApplicable === "—"
      ? "—"
      : `${derived.advanceApplicable} · ${derived.advancePercent}`;

  return (
    <Paper
      variant="outlined"
      sx={{ borderColor: "#E0E4EF", borderRadius: "8px", mb: 2, p: 2 }}
    >
      <Typography sx={{ fontSize: 13, fontWeight: 700, mb: 2 }}>
        Commercial terms (review context)
      </Typography>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 2,
          mb: 2,
        }}
      >
        <LabelValue label="Credit days" value={creditDays || "—"} />
        <LabelValue label="Pattern" value={patternName || "—"} />
        <LabelValue label="First tranche basis" value={basisLabel || "—"} />
        <LabelValue label="Advance" value={advanceCombined} />
      </Box>
      <Divider sx={{ mb: 1.5 }} />
      <Typography sx={{ fontSize: 13, color: "text.primary", lineHeight: 1.5 }}>
        {derived.remarks}
      </Typography>
    </Paper>
  );
}

export function CmReviewPanel({ task, onSubmitted }: Props) {
  const [remarks, setRemarks] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const taskTypeQuery = useReviewerTaskType(task.task_type_id);
  const remarksMaxLength = useRemarksMaxLength(
    reviewerFormId(taskTypeQuery.data?.task_config, "cm"),
    REVIEWER_REMARKS_FIELD.cm,
  );

  const vmEnrichmentFormId = transitionForms(
    taskTypeQuery.data?.task_config,
  )?.vm_enrichment;

  const vmEnrichmentQuery = useQuery({
    queryKey: ["reviewer-vm-enrichment", vmEnrichmentFormId, task.id],
    queryFn: async () => {
      const [schema, rows] = await Promise.all([
        taskerService.getFormSchema(
          vmEnrichmentFormId as string,
          task.id,
          "reviewer",
        ),
        taskerService.searchFormData(
          task.id,
          vmEnrichmentFormId as string,
          "reviewer",
        ),
      ]);
      const latest = [...rows].sort((a, b) => {
        const ta = new Date((a.created_at as string) ?? 0).getTime();
        const tb = new Date((b.created_at as string) ?? 0).getTime();
        return tb - ta;
      })[0];
      return {
        fields: schema.fields,
        data: (latest?.form_data ?? {}) as Record<string, unknown>,
      };
    },
    enabled: Boolean(vmEnrichmentFormId && task.id),
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    retry: false,
  });

  const commercialTerms = useMemo(() => {
    const fields = vmEnrichmentQuery.data?.fields ?? [];
    const data = vmEnrichmentQuery.data?.data ?? {};
    const byName = new Map(fields.map((f) => [f.name, f]));
    const nameFor = (fieldName: string): string | undefined => {
      const raw = data[fieldName];
      if (raw == null || raw === "") return undefined;
      return String(raw);
    };
    const labelFor = (fieldName: string): string | undefined => {
      const field = byName.get(fieldName);
      const raw = data[fieldName];
      if (raw == null || raw === "") return undefined;
      return optionDisplayName(field, raw) ?? String(raw);
    };
    return {
      creditDays: nameFor("credit_days"),
      patternName: nameFor("payment_terms_pattern"),
      basisName: nameFor("first_tranche_payment_basis"),
      basisLabel: labelFor("first_tranche_payment_basis"),
    };
  }, [vmEnrichmentQuery.data]);

  const runTransition = async (
    action: "cm_approve" | "cm_reject" | "cm_go_back",
    reason?: string,
  ) => {
    if (!task.id) return;
    setSubmitting(true);
    setError(null);
    try {
      const decision =
        action === "cm_approve"
          ? "Approve"
          : action === "cm_go_back"
          ? "Send Back"
          : "Reject";
      const form_data = {
        category_manager_decision: decision,
        category_manager_remarks: reason ?? remarks,
      };
      await taskerService.executeTransition(
        task.id,
        action,
        form_data,
        "reviewer",
      );
      const outcome = action === "cm_approve" ? "approved" : "rejected";
      const message =
        action === "cm_approve"
          ? REVIEWER_APPROVED_MSG.cm
          : action === "cm_go_back"
          ? REVIEWER_SEND_BACK_MSG_TO_VM
          : REVIEWER_REJECTED_MSG;
      onSubmitted(outcome, message);
    } catch (err) {
      setError(readErrorMessage(err, "Couldn't submit decision. Try again."));
      // Re-throw so reject/send-back callers (the footer's async confirm)
      // see the failure and keep the dialog open with the typed reason.
      throw err;
    } finally {
      setSubmitting(false);
    }
  };

  const handleApprove = () => {
    // Approve doesn't come from a dialog with a reason to preserve — the
    // panel-level error banner (setError above) is enough.
    void runTransition("cm_approve").catch(() => {});
  };

  const handleReject = (reason: string) => runTransition("cm_reject", reason);

  const handleSendBack = (reason: string) =>
    runTransition("cm_go_back", reason);

  if (taskTypeQuery.isInitialLoading || vmEnrichmentQuery.isInitialLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  // Prerequisite failed: either the task-type fetch errored, or it succeeded
  // but the `vm_enrichment` entry is missing (task_config shape drift, BE
  // sending JSON string with unexpected content, etc.). In both cases the
  // dependent vmEnrichmentQuery is disabled and never surfaces isError, so
  // without this branch the CM would see a confident but blank review context.
  if (
    taskTypeQuery.isError ||
    (taskTypeQuery.data && !vmEnrichmentFormId)
  ) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          Couldn&apos;t load the VM-enriched review context. Refresh to retry.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography sx={{ fontSize: 15, fontWeight: 700, mb: 0.5 }}>
        Category / commercial review
      </Typography>
      <Typography variant="body2" sx={{ color: "text.secondary", mb: 2.5 }}>
        All vendor-submitted and VM-enriched data is visible for review. Core
        fields are view-only.
      </Typography>

      <PriorReviewerRemarks
        task={task}
        taskConfig={taskTypeQuery.data?.task_config}
        authMode="reviewer"
        excludeRole="cm"
      />

      {vmEnrichmentQuery.isError && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Couldn't load VM-enriched context — commercial-terms and quotation
          cards will be blank. Decision can still be recorded.
        </Alert>
      )}

      <CommercialTermsCard
        creditDays={commercialTerms.creditDays}
        patternName={commercialTerms.patternName}
        basisName={commercialTerms.basisName}
        basisLabel={commercialTerms.basisLabel}
      />

      <VmEnrichmentSummary
        formId={vmEnrichmentFormId}
        taskId={task.id}
        hiddenSectionKeys={CM_HIDDEN_SECTIONS}
        hiddenFieldKeys={CM_HIDDEN_FIELDS}
      />

      <Box sx={{ mt: 3 }}>
        <Typography sx={{ fontSize: 13, fontWeight: 600, mb: 0.75 }}>
          Category Manager remarks
        </Typography>
        <MuiTextField
          fullWidth
          multiline
          minRows={3}
          size="small"
          value={remarks}
          onChange={(e) => setRemarks(clampText(e.target.value, remarksMaxLength))}
          placeholder="Optional for approve; the reject dialog collects a mandatory reason separately."
          inputProps={{ autoComplete: "cm-remarks", maxLength: remarksMaxLength }}
          sx={{
            "& .MuiInputBase-input": { fontSize: 13 },
          }}
        />
        <CharCounter length={remarks.length} max={remarksMaxLength} />
      </Box>

      <ReviewerActionsFooter
        reasonMaxLength={remarksMaxLength}
        submitting={submitting}
        errorMessage={error}
        onApprove={handleApprove}
        onReject={handleReject}
        onSendBack={handleSendBack}
        approveLabel="Approve"
        rejectPromptTitle="Reject onboarding"
        sendBackPromptTitle="Send back to Vendor Manager"
        sendBackPromptPlaceholder="Reason to send back (Vendor Manager will see this)"
      />
    </Box>
  );
}

export default CmReviewPanel;
